import React, { VFC } from 'react';
import { useTranslation } from 'react-i18next';
import { CardCredit, Typography } from '@dxb-web-ui-framework/design-system';
import { formatAmount } from '@dxb-web-ui-framework/common/utils/Math';
import { Card as CardType } from '../domain/Card/entities/Card';
import { CardSize } from '../contexts/Card';
import { CardProductAssets, CardStatuses } from '../types';
import { Literal } from '../domain/Card/entities/IssueCard/CardIssueProduct';
import { mappingCardStatuses } from '../utils/mappingCardStatuses';
import { getCardImage } from '../utils';

export type PlaceholderData = {
  holderName?: string;
  title?: string;
  literals?: Literal;
  backgroundFront?: string;
  hasBlackFont?: boolean;
  assets?: CardProductAssets[];
};

export type CardProps = {
  size: CardSize;
  cardData?: CardType;
  isFlipped?: boolean;
  onClick: () => void;
  isCollapsed?: boolean;
  hasSkeleton?: boolean;
  placeholderData?: PlaceholderData;
  onFlipCard?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  disableHoverState?: boolean;
  cardImageSize?: 'small' | 'medium' | 'big';
};

export const Card: VFC<CardProps> = ({
  size,
  onClick,
  cardData,
  hasSkeleton,
  placeholderData,
  isFlipped = false,
  isCollapsed = false,
  disableHoverState = false,
  cardImageSize = 'small',
}) => {
  const { t } = useTranslation();
  const locale = t('global.settings.date.locale');

  if (cardData) {
    const {
      cardId,
      cardType,
      cardNumber,
      virtual,
      cvv,
      cardName,
      expiryDate,
      cardNetwork,
      description,
      cardStatus,
      pendingAuthorizations,
      availableBalance,
      assets,
    } = cardData;

    //Card Statuses
    const isPending = [CardStatuses.Inactive, CardStatuses.In_Progress].some(
      cardStatusType => cardStatusType === cardStatus,
    );
    const isPaused = cardStatus === CardStatuses.Blocked;
    const isTerminated = [
      CardStatuses.Lost,
      CardStatuses.Stolen,
      CardStatuses.Fraud,
      CardStatuses.Closed,
      CardStatuses.Not_Received,
      CardStatuses.Damaged,
      CardStatuses.Pin_Tries_Exceeded,
    ].some(cardStatusType => cardStatusType === cardStatus);

    //Values calculations
    const availableBalanceValue = formatAmount(
      availableBalance.value,
      availableBalance.currency,
      availableBalance.decimalPointPosition ?? 2,
      locale,
    );
    const pendingAuthorizationsValue = formatAmount(
      pendingAuthorizations.value,
      pendingAuthorizations.currency,
      pendingAuthorizations.decimalPointPosition ?? 2,
      locale,
    );

    //Switch function to get the label for each button label of CardCredit
    const buttonLabel = mappingCardStatuses(t)[cardStatus].filterStatus.label;

    return (
      <>
        <CardCredit
          cardId={cardId}
          cvv={cvv}
          size={size}
          hasSkeleton={hasSkeleton}
          name={cardName.holderName}
          onClick={onClick}
          number={cardNumber}
          isVirtual={virtual}
          isPaused={isPaused}
          isPending={isPending}
          isTerminated={isTerminated}
          descriptionValue={description}
          availableBalanceValue={availableBalanceValue}
          expirationDate={expiryDate}
          backgroundFront={getCardImage(assets, cardImageSize)}
          backgroundBack={getCardImage(assets, cardImageSize)}
          backgroundCollapsed={getCardImage(assets, `${cardImageSize}-collapsed`)}
          isCollapsed={isCollapsed}
          pausedButtonLabel={buttonLabel}
          terminatedButtonLabel={buttonLabel}
          pendingButtonLabel={buttonLabel}
          pendingAuthorizationsValue={pendingAuthorizationsValue}
          descriptionLabel={t('Card_Component.DescriptionLabel')}
          expirationCardLabel={t('Card_Component.ExpirationCardLabel')}
          cardDetailsButtonLabel={t('Card_Component.CardDetailsLabel')}
          availableBalanceLabel={t('Card_Component.AvailableBalanceLabel')}
          pendingAuthorizationsLabel={t('Card_Component.PendingAuthorizationsLabel')}
          isHightlightedDetails={false}
          isFlipped={isFlipped}
          disableHoverState={disableHoverState}
          ariaControls={`card-credit-${cardId}`}
          hasBlackFont={cardData.hasBlackFont}
        />
        <Typography variant={'srOnly'}>
          {` ${cardNetwork} ${cardType} `} {isPaused && buttonLabel} {isPending && buttonLabel}{' '}
          {isTerminated && buttonLabel}
        </Typography>
      </>
    );
  }

  const { holderName, title, backgroundFront, hasBlackFont, assets } = placeholderData ?? {
    holderName: '',
    title: '',
    backgroundFront: '',
    hasBlackFont: false,
  };

  // Placeholder card
  return (
    <CardCredit
      cardId={''}
      cvv={'***'}
      size={size}
      isPaused={false}
      isVirtual={false}
      isPending={false}
      onClick={() => {}}
      hasSkeleton={false}
      isTerminated={false}
      name={holderName || ''}
      expirationDate={'**/**'}
      number={'****************'}
      descriptionValue={title || ''}
      availableBalanceValue={''}
      backgroundBack={''}
      isCollapsed={false}
      pausedButtonLabel={''}
      pendingButtonLabel={''}
      backgroundCollapsed={''}
      terminatedButtonLabel={''}
      pendingAuthorizationsValue={''}
      backgroundFront={(assets ? getCardImage(assets, cardImageSize) : backgroundFront) || ''}
      descriptionLabel={t('Card_Component.DescriptionLabel')}
      expirationCardLabel={t('Card_Component.ExpirationCardLabel')}
      cardDetailsButtonLabel={t('Card_Component.CardDetailsLabel')}
      availableBalanceLabel={t('Card_Component.AvailableBalanceLabel')}
      pendingAuthorizationsLabel={t('Card_Component.PendingAuthorizationsLabel')}
      isHightlightedDetails={false}
      isFlipped={false}
      disableHoverState={disableHoverState}
      ariaControls={'card-credit-placeholder'}
      hasBlackFont={hasBlackFont}
    />
  );
};
